import { useMemo, useRef, useState } from "react";
import { ReportMissingSiteButton } from "./ReportMissingSiteButton";
import { AddressSearchInput } from "@/components/AddressSearchInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import { canManageCustomerSites } from "@shared/customerSiteGovernance";
import { Archive, MapPin, Pencil, RotateCcw, Star } from "lucide-react";
import ReportIssueButton from "@/components/errors/ReportIssueButton";
import { toast } from "sonner";

type SiteRow = {
  id: number;
  label: string | null;
  address: string | null;
  unitNumber: string | null;
  postalCode: string | null;
  isDefault: boolean;
};

function formatSite(site: SiteRow) {
  return [site.unitNumber, site.address, site.postalCode && !site.address?.includes(site.postalCode) ? `(${site.postalCode})` : null]
    .filter(Boolean)
    .join(" ");
}

function nextOccurrenceId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `occ-${Date.now()}`;
}

const TOUCH = "min-h-11 min-w-11";

export function CustomerServiceSitesPanel({
  customerId,
  salespersonId,
  actor,
}: {
  customerId: number;
  salespersonId: number | null | undefined;
  actor: { id: number; role: string } | null | undefined;
}) {
  const uiCanManage = Boolean(actor && canManageCustomerSites(actor, { salespersonId: salespersonId ?? null }));
  const [showAdd, setShowAdd] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [archiveId, setArchiveId] = useState<number | null>(null);
  const [confirmLast, setConfirmLast] = useState(false);
  const [replacementId, setReplacementId] = useState<number | "">("");
  const [label, setLabel] = useState("");
  const [address, setAddress] = useState("");
  const [unitNumber, setUnitNumber] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [highlightedId, setHighlightedId] = useState<number | null>(null);
  const occurrenceRef = useRef(nextOccurrenceId());

  const query = trpc.customerSites.listGoverned.useQuery(
    { customerId, includeArchived: uiCanManage },
    { enabled: customerId > 0, placeholderData: (previous) => previous },
  );
  const activeSites = query.data?.activeSites ?? [];
  const archivedSites = query.data?.archivedSites ?? [];
  const canManage = query.data?.canManage ?? uiCanManage;

  const add = trpc.customerSites.add.useMutation({
    onSuccess: async (res) => {
      if (res.alreadyRegistered) {
        toast.message(res.message ?? "This site is already registered");
        setHighlightedId(res.site.id);
      } else {
        toast.success("Site added");
        setShowAdd(false);
        setLabel("");
        setAddress("");
        setUnitNumber("");
        setPostalCode("");
      }
      occurrenceRef.current = nextOccurrenceId();
      await query.refetch();
    },
    onError: (err) => toast.error(err.message),
  });
  const update = trpc.customerSites.update.useMutation({
    onSuccess: async (res) => {
      if (res.alreadyRegistered) {
        toast.message(res.message ?? "This site is already registered");
        setHighlightedId(res.site.id);
      } else {
        toast.success("Site updated");
        setEditingId(null);
      }
      occurrenceRef.current = nextOccurrenceId();
      await query.refetch();
    },
    onError: (err) => toast.error(err.message),
  });
  const setDefault = trpc.customerSites.setDefault.useMutation({
    onSuccess: async () => {
      toast.success("Default service site updated");
      await query.refetch();
    },
    onError: (err) => toast.error(err.message),
  });
  const archive = trpc.customerSites.archive.useMutation({
    onSuccess: async () => {
      toast.success("Site archived. Historical SVRs keep this location.");
      setArchiveId(null);
      setConfirmLast(false);
      await query.refetch();
    },
    onError: (err) => toast.error(err.message),
  });
  const restore = trpc.customerSites.restore.useMutation({
    onSuccess: async () => {
      toast.success("Site restored");
      await query.refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const archiveTarget = useMemo(
    () => activeSites.find((site) => site.id === archiveId) ?? null,
    [activeSites, archiveId],
  );

  const fields = (
    <div className="space-y-2 min-w-0">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input aria-label="Site label" placeholder="Label (e.g. Office)" value={label} onChange={(e) => setLabel(e.target.value)} className="h-11 text-sm min-w-0" />
        <Input aria-label="Unit number" placeholder="Unit number" value={unitNumber} onChange={(e) => setUnitNumber(e.target.value)} className="h-11 text-sm min-w-0" />
      </div>
      <Input aria-label="Postal code" placeholder="Postal code" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} className="h-11 text-sm min-w-0" />
      <AddressSearchInput value={address || postalCode} placeholder="Search by address or postal code..." onAddressSelect={(nextAddress, nextPostal) => { setAddress(nextAddress); setPostalCode(nextPostal); }} />
    </div>
  );

  return (
    <div className="border-t pt-3 min-w-0 overflow-x-hidden">
      <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Service sites</p>
        {canManage && (
          <button type="button" aria-label="Add site" className={`flex items-center justify-center gap-1 text-xs font-medium ${TOUCH} px-2`} onClick={() => { setShowAdd(true); setEditingId(null); setLabel(""); setAddress(""); setUnitNumber(""); setPostalCode(""); occurrenceRef.current = nextOccurrenceId(); }}>
            Add Site
          </button>
        )}
      </div>

      <ReportMissingSiteButton customerId={customerId} canReview={actor?.role === "admin"} canManage={canManage} />
      {query.isLoading && !query.data && <p className="text-xs text-muted-foreground" role="status">Loading service sites…</p>}
      {query.isError && !query.data && (
        <div className="text-xs text-red-600 space-y-2">
          <p>Could not load service sites.</p>
          <Button className={TOUCH} size="sm" variant="outline" onClick={() => query.refetch()}>Retry</Button>
        </div>
      )}
      {query.isError && query.data && (
        <div className="text-xs text-amber-700 mb-2 flex items-center gap-2">
          <span>Showing last loaded sites. Refresh failed.</span>
          <Button className={TOUCH} size="sm" variant="outline" onClick={() => query.refetch()}>Retry</Button>
        </div>
      )}
      {query.isSuccess && activeSites.length === 0 && !showAdd && (
        <p className="text-xs text-muted-foreground italic">No service sites yet. The primary customer address remains the fallback.</p>
      )}

      <div className="space-y-2 min-w-0">
        {activeSites.map((site) => (
          <div key={site.id} className={`border rounded-md p-2.5 min-w-0 ${highlightedId === site.id ? "ring-2 ring-amber-500" : ""}`}>
            {editingId === site.id ? (
              <div className="space-y-2">
                {fields}
                <div className="flex flex-wrap gap-2">
                  <Button className={TOUCH} size="sm" disabled={update.isPending} onClick={() => update.mutate({ siteId: site.id, label, address, unitNumber, postalCode, occurrenceId: occurrenceRef.current })}>Save</Button>
                  <Button className={TOUCH} size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                  {update.isError && (
                    <>
                      <Button className={TOUCH} size="sm" variant="outline" onClick={() => update.mutate({ siteId: site.id, label, address, unitNumber, postalCode, occurrenceId: occurrenceRef.current })}>Retry</Button>
                      <ReportIssueButton surface="sales.customer_card.service_sites" safeErrorCode={update.error?.data?.code ?? "INTERNAL_SERVER_ERROR"} priorDataRetained />
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-2 min-w-0">
                <div className="flex items-start gap-2 min-w-0">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide break-words">
                      {site.label || "Service site"}
                      {site.isDefault && <span className="ml-1.5 text-amber-600">★ Default service site</span>}
                    </p>
                    <p className="font-medium text-sm break-words">{formatSite(site)}</p>
                  </div>
                </div>
                {canManage && (
                  <div className="flex items-center gap-1 shrink-0">
                    {!site.isDefault && (
                      <button type="button" aria-label="Set as default service site" className={`${TOUCH} p-2 text-muted-foreground hover:text-amber-500`} onClick={() => setDefault.mutate({ siteId: site.id, occurrenceId: nextOccurrenceId() })}>
                        <Star className="h-4 w-4" />
                      </button>
                    )}
                    <button type="button" aria-label={`Edit ${site.label || "service site"}`} className={`${TOUCH} p-2 text-muted-foreground hover:text-foreground`} onClick={() => { setEditingId(site.id); setShowAdd(false); setLabel(site.label || ""); setAddress(site.address || ""); setUnitNumber(site.unitNumber || ""); setPostalCode(site.postalCode || ""); occurrenceRef.current = nextOccurrenceId(); }}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button type="button" aria-label={`Archive ${site.label || "service site"}`} className={`${TOUCH} p-2 text-muted-foreground hover:text-red-500`} onClick={() => { setArchiveId(site.id); setConfirmLast(false); setReplacementId(""); }}>
                      <Archive className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {showAdd && canManage && (
        <div className="border border-dashed rounded-md p-3 mt-2 space-y-2 min-w-0">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">New service site</p>
          {fields}
          <div className="flex flex-wrap gap-2">
            <Button className={TOUCH} size="sm" disabled={add.isPending || !address.trim()} onClick={() => add.mutate({ customerId, label, address, unitNumber, postalCode, occurrenceId: occurrenceRef.current })}>
              {add.isPending ? "Adding…" : "Add Site"}
            </Button>
            <Button className={TOUCH} size="sm" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            {add.isError && (
              <>
                <Button className={TOUCH} size="sm" variant="outline" onClick={() => add.mutate({ customerId, label, address, unitNumber, postalCode, occurrenceId: occurrenceRef.current })}>Retry</Button>
                <ReportIssueButton surface="sales.customer_card.service_sites" safeErrorCode={add.error?.data?.code ?? "INTERNAL_SERVER_ERROR"} priorDataRetained />
              </>
            )}
          </div>
        </div>
      )}

      {archiveTarget && (
        <div className="mt-3 border rounded-md p-3 space-y-2 bg-muted/40 min-w-0" role="dialog" aria-label="Archive service site">
          <p className="text-sm font-medium">Archive this service site?</p>
          <p className="text-xs text-muted-foreground">Historical SVRs keep this location. The site will not be selectable for new SVRs.</p>
          {activeSites.length === 1 && (
            <label className="flex items-start gap-2 text-xs">
              <input type="checkbox" className="mt-1 h-4 w-4" checked={confirmLast} onChange={(e) => setConfirmLast(e.target.checked)} />
              This is the last active service site. I understand new SVRs will have no stored site until another is added.
            </label>
          )}
          {archiveTarget.isDefault && activeSites.filter((site) => site.id !== archiveTarget.id).length > 1 && (
            <label className="block text-xs">
              Replacement default
              <select aria-label="Replacement default service site" className="mt-1 w-full h-11 border rounded-md px-2" value={replacementId} onChange={(e) => setReplacementId(e.target.value ? Number(e.target.value) : "")}>
                <option value="">Select a replacement</option>
                {activeSites.filter((site) => site.id !== archiveTarget.id).map((site) => (
                  <option key={site.id} value={site.id}>{site.label || formatSite(site)}</option>
                ))}
              </select>
            </label>
          )}
          {(() => {
            const needsLastAck = activeSites.length === 1 && !confirmLast;
            const needsReplacement =
              Boolean(archiveTarget.isDefault) &&
              activeSites.filter((site) => site.id !== archiveTarget.id).length > 1 &&
              replacementId === "";
            const archiveBlockedReason = needsLastAck
              ? "Confirm that this is the last active service site before archiving."
              : needsReplacement
                ? "Select a replacement default service site before archiving."
                : null;
            return (
              <>
                {archiveBlockedReason && (
                  <p className="text-xs text-amber-800" role="status">{archiveBlockedReason}</p>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    className={TOUCH}
                    size="sm"
                    disabled={archive.isPending || Boolean(archiveBlockedReason)}
                    onClick={() =>
                      archive.mutate({
                        siteId: archiveTarget.id,
                        confirmLastActive: confirmLast,
                        replacementDefaultSiteId: replacementId === "" ? undefined : replacementId,
                        occurrenceId: nextOccurrenceId(),
                      })
                    }
                  >
                    Archive
                  </Button>
                  <Button className={TOUCH} size="sm" variant="outline" onClick={() => setArchiveId(null)}>Cancel</Button>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {canManage && archivedSites.length > 0 && (
        <div className="mt-3 min-w-0">
          <button type="button" className={`${TOUCH} text-xs underline`} onClick={() => setShowArchived((value) => !value)}>
            {showArchived ? "Hide archived sites" : `Show archived sites (${archivedSites.length})`}
          </button>
          {showArchived && (
            <div className="space-y-2 mt-2">
              {archivedSites.map((site) => (
                <div key={site.id} className="border rounded-md p-2.5 text-sm flex items-start justify-between gap-2 min-w-0">
                  <p className="text-muted-foreground break-words min-w-0">{formatSite(site)} · archived</p>
                  <button type="button" aria-label={`Restore ${site.label || "service site"}`} className={`${TOUCH} p-2`} onClick={() => restore.mutate({ siteId: site.id, occurrenceId: nextOccurrenceId() })}>
                    <RotateCcw className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
